import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { UserAdapter } from '../models/adapter/user.adapter';
import { User } from '../models/user.model';
import { ApiUrl } from './api.url';
import { TerminalStateService } from './terminal-state.service';

@Injectable({
    providedIn: 'root'
})
export class AuthService {

    /**
     * Ctor
     * @ignore
     */
    constructor(
        private readonly http: HttpClient,
        private readonly userAdapter: UserAdapter,
        private readonly terminalStateService: TerminalStateService) { }

    private readonly tokenKeys = ['stats-accessToken', 'stats-refreshToken', 'stats-user'] as const;
    private refreshInFlight = false;

    /**
     * Permet de se déconnecter
     */
    public logout(): Observable<void> {
        const URL = `${environment.baseUrl}${environment.apiPath}${ApiUrl.LOGOUT}`;
        return this.http.get(URL)
            .pipe(
                map(() => {
                    this.deleteTokens();
                }),
                catchError(() => {
                    // En cas d'erreur API, on supprime quand même les tokens locaux
                    this.deleteTokens();
                    return of(undefined);
                })
            )
    }

    /**
     * Permet de supprimer les tokens du localstorage
     */
    public deleteTokens(): void {
        this.tokenKeys.forEach(key => {
            localStorage.removeItem(key);
            sessionStorage.removeItem(key);
        });
        // Effacer aussi le terminal sélectionné lors de la déconnexion
        this.terminalStateService.clearSavedTerminal();
    }

    /**
     * Permet de récupérer l'utilisateur authentifié
     * @returns Utilisateur courant
     */
    public getConnectedUser(): User | null {
        let userString = this.getItem('stats-user');
        if (userString) {
            return this.userAdapter.adapt(JSON.parse(userString));
        }
        return null;
    }

    /**
     * Permet de s'authentifier
     * @param email Adresse email de l'utilisateur
     * @param password Mot de passe de l'utilisateur
     * @returns Valeur indiquant si l'utilisateur est authentifié ou non
     */
    public login(email: string, password: string, rememberMe = false): Observable<boolean | null> {
        const body = {
            email,
            password
        }
        return this.http.post(`${environment.baseUrl}${environment.apiPath}${ApiUrl.LOGIN}`, body).pipe(
            map((response: any) => {
                if (response.accessToken) {
                    this.deleteTokens();
                    const storage = rememberMe ? localStorage : sessionStorage;
                    storage.setItem('stats-accessToken', response.accessToken);
                    storage.setItem('stats-refreshToken', response.refreshToken);
                    storage.setItem('stats-user', JSON.stringify(response.user));
                    return true;
                }
                return false;
            }),
            catchError(err => of(null)),
        );
    }

    /**
     * Permet de récupérer de nouveau token si l'access à expiré
     */
    public refreshTokens(): Observable<string> {
        const user = this.getConnectedUser();
        const refreshToken = this.getItem('stats-refreshToken');

        if (!user || !refreshToken) {
            return of('');
        }

        const url = `${environment.baseUrl}${environment.apiPath}${ApiUrl.REFRESH}`;
        const params = {
            userId: String(user.id),
            refreshToken: refreshToken,
        };

        return this.http.post(url, null, { params, headers: { Authorization: `Bearer ${refreshToken}` } })
            .pipe(
                map((jwts: any) => {
                    if (!jwts?.accessToken || !jwts?.refreshToken) {
                        throw new Error('Invalid refresh response');
                    }
                    const storage = this.getStorageForExistingTokens();
                    storage.setItem('stats-accessToken', jwts.accessToken);
                    storage.setItem('stats-refreshToken', jwts.refreshToken);
                    return jwts.accessToken;
                })
            );
    }

    /**
     * Remonte une valeur indiquant si l'utilisateur est authentifié ou non
     * @returns Authentifié ou non ?
     */
    public isLoggedIn(): boolean {
        const atoken = this.getItem('stats-accessToken');
        const rtoken = this.getItem('stats-refreshToken');
        const user = this.getItem('stats-user');

        if (!atoken || !rtoken || !user || atoken.length === 0 || rtoken.length === 0) {
            return false;
        }

        // Vérifier si le token JWT n'est pas expiré
        const isTokenValid = this.isTokenValid(atoken);
        if (isTokenValid) {
            return true;
        }

        // Access token expiré, tenter un refresh silencieux
        if (!this.refreshInFlight) {
            this.refreshInFlight = true;
            this.refreshTokens().subscribe({
                next: () => { this.refreshInFlight = false; },
                error: () => {
                    this.refreshInFlight = false;
                    this.deleteTokens();
                },
            });
        }

        // Autoriser temporairement l'utilisateur pendant le refresh ; le guard vérifiera à nouveau ensuite.
        return true;
    }

    private isTokenValid(token: string | null): boolean {
        if (!token) {
            return false;
        }

        try {
            const tokenPayload = JSON.parse(atob(token.split('.')[1]));
            const currentTime = Math.floor(Date.now() / 1000);

            if (tokenPayload.exp && tokenPayload.exp < currentTime) {
                return false;
            }

            return true;
        } catch (error) {
            return false;
        }
    }

    /**
     * Récupère le token d'accès depuis le localStorage
     * @returns Token d'accès JWT ou null si non disponible
     */
    public getAccessToken(): string | null {
        return this.getItem('stats-accessToken');
    }

    /**
     * Récupère le refresh token depuis le stockage persistant
     * @returns Refresh token JWT ou null si non disponible
     */
    public getRefreshToken(): string | null {
        return this.getItem('stats-refreshToken');
    }

    private getItem(key: string): string | null {
        return localStorage.getItem(key) ?? sessionStorage.getItem(key);
    }

    private getStorageForExistingTokens(): Storage {
        if (this.hasTokenInStorage(localStorage)) {
            return localStorage;
        }
        if (this.hasTokenInStorage(sessionStorage)) {
            return sessionStorage;
        }
        return localStorage;
    }

    private hasTokenInStorage(storage: Storage): boolean {
        return Boolean(storage.getItem('stats-accessToken'));
    }
}
